import {
  onlineManager,
  useIsFetching,
  useIsMutating,
  useMutationState,
} from "@tanstack/react-query";

import { useAuth } from "~/hooks/useAuth";

export type SyncStatus = {
  state: "signed-out" | "loading" | "syncing" | "offline" | "synced";
  label: string;
  hint: string;
};

/**
 * Offline-first: the cache is persisted and writes are queued, so a failure to
 * reach the server means "not uploaded yet", never "lost". This reflects
 * pending writes and connectivity.
 */
export function useSyncStatus(): SyncStatus {
  const { user } = useAuth();
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  const paused = useMutationState({
    filters: { predicate: (m) => m.state.isPaused },
  }).length;

  if (!user) {
    return {
      state: "signed-out",
      label: "Not signed in",
      hint: "Sign in to sync",
    };
  }
  if (paused > 0 || !onlineManager.isOnline()) {
    return {
      state: "offline",
      label: "Saved on this device",
      hint: "Will sync when you're back online",
    };
  }
  if (mutating > 0) {
    return {
      state: "syncing",
      label: "Syncing",
      hint: "Sending your latest changes",
    };
  }
  if (fetching > 0) {
    return {
      state: "loading",
      label: "Loading",
      hint: "Opening your collections",
    };
  }
  return {
    state: "synced",
    label: "All synced",
    hint: "Everything is backed up",
  };
}
